
import { Vue, Options, prop } from "vue-class-component";
import PillOverlay from "@/app/ui/components/pill-overlay/index.vue";
import Checkbox from "primevue/checkbox";
class Props {
  originAirportOptions = prop<string[]>({ default: [], type: Array });
  destinationAirportOptions = prop<string[]>({ default: [], type: Array });
  selectedOriginAirports = prop<string[]>({ default: [], type: Array });
  selectedDestinationAirports = prop<string[]>({ default: [], type: Array });
  selectedLegs = prop<number[]>({ default: [], type: Array });
  selectedSort = prop<string>({ default: "", type: String });
  selectedThresholdTime = prop<number>({ default: 1440, type: Number });
  disabledFlight = prop<boolean>({ default: false, type: Boolean });
}

type Option = {
  value: string;
  label: string;
};

@Options({
  components: { PillOverlay, Checkbox },
  emits: [
    "change",
    "update:selectedThresholdTime",
    "update:selectedSort",
    "update:selectedLegs",
    "update:selectedOriginAirports",
    "update:selectedDestinationAirports",
  ],
})
export default class FilterFlight extends Vue.with(Props) {
  // airports
  get originAirportOptionsValue(): Option[] {
    return this.originAirportOptions
      .map((airport) => {
        if (typeof airport === "string") {
          return {
            value: airport,
            label: airport,
          };
        }
        return airport;
      })
      .filter((airport) => airport !== undefined);
  }
  get destinationAirportOptionsValue(): Option[] {
    return this.destinationAirportOptions
      .map((airport) => {
        if (typeof airport === "string") {
          return {
            value: airport,
            label: airport,
          };
        }
        return airport;
      })
      .filter((airport) => airport !== undefined);
  }
  get originAirportDisplay(): string {
    return this.selectedOriginAirports.length
      ? (this.$t("cargo.v2.flight.search.filter.originLabel", {
          list: this.selectedOriginAirports.join(", ")
        }) as string)
      : "";
  }
  get destinationAirportDisplay(): string {
    return this.selectedDestinationAirports.length
      ? (this.$t("cargo.v2.flight.search.filter.destinationLabel", {
          list: this.selectedDestinationAirports.join(", ")
        }) as string)
      : "";
  }
  get fullAirportRouteDisplay(): string {
    const origin = this.originAirportDisplay;
    const destination = this.destinationAirportDisplay;

    if (origin && destination) {
      return this.$t("cargo.v2.flight.search.filter.airportRoute", {
        origin,
        destination
      }) as string;
    } else if (origin) {
      return this.$t("cargo.v2.flight.search.filter.airportOriginOnly", {
        origin
      }) as string;
    } else if (destination) {
      return this.$t("cargo.v2.flight.search.filter.airportDestinationOnly", {
        destination
      }) as string;
    }

    return "";
  }
  tempOriginAirports: string[] = [];
  tempDestinationAirports: string[] = [];
  set externalSelectedOriginAirports(value: string[]) {
    this.$emit("update:selectedOriginAirports", value);
    this.$emit("change");
  }
  set externalSelectedDestinationAirports(value: string[]) {
    this.$emit("update:selectedDestinationAirports", value);
    this.$emit("change");
  }
  get airportsActive(): boolean {
    return (
      !!this.selectedOriginAirports.length ||
      !!this.selectedDestinationAirports.length
    );
  }
  onToggleAirports(isOpen: boolean) {
    if (isOpen) {
      this.tempOriginAirports = this.selectedOriginAirports;
      this.tempDestinationAirports = this.selectedDestinationAirports;
    }
  }
  onResetAirports() {
    this.tempOriginAirports = [];
    this.tempDestinationAirports = [];
    this.externalSelectedOriginAirports = [];
    this.externalSelectedDestinationAirports = [];
  }
  onApplyAirports() {
    this.externalSelectedOriginAirports = this.tempOriginAirports;
    this.externalSelectedDestinationAirports = this.tempDestinationAirports;
  }
  get isApplyDisabled(): boolean {
    const isOriginChanged =
      this.tempOriginAirports?.sort()?.join(",") !==
      this.selectedOriginAirports?.sort()?.join(",");
    const isDestinationChanged =
      this.tempDestinationAirports?.sort()?.join(",") !==
      this.selectedDestinationAirports?.sort()?.join(",");
    return !isOriginChanged && !isDestinationChanged;
  }
  get isApplyDisabledLegs(): boolean {
    const isLegsChanged =
      this.tempLegs?.sort()?.join(",") !== this.selectedLegs?.sort()?.join(",");
    return !isLegsChanged;
  }
  // legs
  tempLegs: number[] = [];
  get legsOptions() {
    return [
      {
        value: 0,
        label: this.$t("cargo.v2.flight.search.filter.direct") as string
      },
      {
        value: 1,
        label: this.$t("cargo.v2.flight.search.filter.transit1") as string
      },
      {
        value: 2,
        label: this.$t("cargo.v2.flight.search.filter.transit2") as string
      },
      {
        value: 3,
        label: this.$t("cargo.v2.flight.search.filter.transit3") as string
      }
    ];
  }
  get legsLabel() {
    return this.selectedLegs
      .map(
        (leg) => this.legsOptions.find((option) => option.value === leg)?.label,
      )
      .join(", ");
  }
  set externalSelectedLegs(value: number[]) {
    this.$emit("update:selectedLegs", value);
    this.$emit("change");
  }
  onToggleLegs(isOpen: boolean) {
    if (isOpen) {
      this.tempLegs = this.selectedLegs;
    }
  }
  onResetLegs() {
    this.tempLegs = [];
    this.externalSelectedLegs = [];
  }
  onApplyLegs() {
    this.externalSelectedLegs = this.tempLegs;
    this.$emit("change");
  }
  get legsActive(): boolean {
    return !!this.selectedLegs.length;
  }
  // sort
  get sortOptions() {
    return [
      {
        value: "eta",
        label: this.$t("cargo.v2.flight.search.filter.fastestEta") as string
      },
      {
        value: "etd",
        label: this.$t("cargo.v2.flight.search.filter.fastestEtd") as string
      }
    ];
  }
  get sortLabel() {
    return (
      this.sortOptions.find((option) => option.value === this.selectedSort)
        ?.label || this.$t("cargo.v2.flight.search.filter.sort")
    );
  }
  get externalSelectedSort(): string {
    return this.selectedSort;
  }
  set externalSelectedSort(value: string) {
    this.$emit("update:selectedSort", value);
    this.$emit("change");
  }
  hasUserChangedSort = false;
  get sortPillActive(): boolean {
    return (
      !!this.selectedSort &&
      (this.hasUserChangedSort || this.selectedSort !== "eta")
    );
  }
  onResetSort() {
    this.hasUserChangedSort = true;
    this.externalSelectedSort = "";
  }
  // threshold time
  get thresholdTimeOptions() {
    return [
      {
        value: 720,
        label: this.$t("cargo.v2.flight.search.filter.hours12") as string
      },
      {
        value: 1440,
        label: this.$t("cargo.v2.flight.search.filter.hours24") as string
      }
    ];
  }
  get thresholdTimeLabel() {
    return (
      this.thresholdTimeOptions.find(
        (option) => option.value === this.selectedThresholdTime
      )?.label || this.$t("cargo.v2.flight.search.filter.hours12")
    );
  }
  get externalSelectedThresholdTime(): number {
    return this.selectedThresholdTime;
  }
  set externalSelectedThresholdTime(value: number) {
    this.$emit("update:selectedThresholdTime", value);
    this.$emit("change");
  }
  get thresholdTimeActive(): boolean {
    return !!this.selectedThresholdTime;
  }
}
